import { useCallback, useMemo, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { LoginPage } from "@/Common/Resources/StaticResourcesFor";
import { AuthenticatorCodeValidator } from "@/Common/Validators/AuthenticatorCodeValidator";

import styles from "./MultifactorAuthenticationInput.module.scss";

interface MultifactorAuthenticationInputProps {
    id?: string,
    dataCy?: string,
    autoFocus?: boolean,
    autoSubmit?: boolean,
    maxLength?: number,
    disabled?: boolean,
    isInvalid?: boolean,
    onChange?: (code: string) => void,
    onSubmit: (code: string) => void
}

export function MultifactorAuthenticationInput({
    id,
    dataCy,
    autoFocus,
    autoSubmit,
    maxLength,
    disabled,
    isInvalid,
    onChange,
    onSubmit
}: Readonly<MultifactorAuthenticationInputProps>): JSX.Element {
    const resourceProvider = useMemo(
        () => {
            const loginPageResources = new LoginPage();

            return new ResourceProvider(loginPageResources);
        },
        []
    );

    const { getText } = useTranslation(resourceProvider);

    const [authenticatorCode, setAuthenticatorCode] = useState<string>("");

    const inputFormInvalidClass = isInvalid ? "invalid" : "";
    const inputFormDisabledClass = disabled ? "disabled" : "";
    const inputFormLetterSpaceClass = authenticatorCode ? styles.letterSpace : "";
    const inputFormCssClass = `form-control ${inputFormInvalidClass} ${inputFormDisabledClass} ${inputFormLetterSpaceClass}`;

    const maxLengthOrDefault = useMemo(() => maxLength ?? AuthenticatorCodeValidator.codeLength, [maxLength]);

    const renderVisualCode = (): string => {
        if (!authenticatorCode) {
            return "";
        }

        const visualArray = Array.from({ length: maxLengthOrDefault }, (_, i) => authenticatorCode[i] || "_");
        return visualArray.join("");
    };

    const handleKeyUp = useCallback((event: React.KeyboardEvent) => {
        if (authenticatorCode && event.key === "Enter") {
            onSubmit(authenticatorCode);
        }
    }, [authenticatorCode, onSubmit]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/\D/g, "").slice(0, maxLengthOrDefault);

        if (authenticatorCode !== value) {
            setAuthenticatorCode(value);
            onChange?.(value);
        }

        if (autoSubmit && value.length >= maxLengthOrDefault) {
            onSubmit(value);
        }
    }, [authenticatorCode, setAuthenticatorCode, autoSubmit, onChange, onSubmit, maxLengthOrDefault]);

    return (
        <div className={styles.authenticatorCodeContainer}>
            <div className={`${styles.visualCode} ${inputFormCssClass}`}>
                {renderVisualCode()}
            </div>
            <input
                type="text"
                inputMode="numeric"
                id={id}
                data-cy={dataCy}
                className={`${styles.authenticatorCode} ${inputFormCssClass}`}
                maxLength={maxLengthOrDefault}
                placeholder={getText("AuthenticatorCodePlaceholder")}
                onKeyUp={handleKeyUp}
                onChange={handleChange}
                value={authenticatorCode}
                autoComplete="off"
                autoFocus={autoFocus}
                disabled={disabled}
            />
        </div>
    );
}
